import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { AutoFitText } from "@/components/ui/auto-fit-text";
import { AnimatedNumber } from "@/components/ui/animated-number";
import { formatCompact, formatSignedPercent } from "@/utils/numberFormat";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

type KpiNumberProps = {
  /** Valor final (numérico) do KPI. */
  value: number;
  /** Formatação (ex.: moeda, percent, inteiro). */
  format: (n: number) => string;
  /**
   * Formatação compacta opcional (ex.: 1,2M) para evitar reduzir fonte demais.
   * Se não for informada, o componente tenta inferir automaticamente.
   */
  compactFormat?: (n: number) => string;
  /**
   * Tamanho fixo do KPI (px). Com a compactação, isso maximiza simetria visual.
   * Observação: por padrão, o componente NÃO auto-ajusta a fonte.
   */
  fontPx?: number;
  /**
   * Se true, reativa o auto-fit por largura (modo antigo).
   * Padrão: false (tamanho fixo).
   */
  autoFit?: boolean;
  /** Duração da animação do contador. */
  durationMs?: number;
  /**
   * Tamanho máximo/mínimo do texto do KPI.
   * Ajuste por contexto (cards pequenos vs cards grandes).
   */
  minFontPx?: number;
  maxFontPx?: number;
  /**
   * "Respiro" interno para garantir que não encoste nas bordas do card.
   * Esse valor é subtraído da largura disponível.
   */
  breathingRoomPx?: number;
  /** Desativa animação e auto-fit (útil em skeletons). */
  disabled?: boolean;
  /** Classe aplicada ao container (tipografia/cor). */
  className?: string;
  /** Elemento HTML de saída (evita `<div>` dentro de `<span>` em tabelas). */
  as?: "div" | "span";
  /** Para acessibilidade, descreve o KPI (ex.: "Receita total"). */
  ariaLabel?: string;
  /** Percentual de variação para exibir abaixo do KPI (comparação). */
  changePercentage?: number | null;
  /** Indicador de tendência: 'up', 'down' ou 'neutral'. */
  trendIndicator?: "up" | "down" | "neutral" | null;
  /** Se true, exibe o indicador de comparação. */
  showComparison?: boolean;
};

/**
 * KPI Number padronizado: contador animado + auto-fit por largura.
 *
 * SRP: este componente orquestra dois comportamentos bem definidos:
 * - animação numérica (`AnimatedNumber`)
 * - ajuste de fonte por largura (`AutoFitText`)
 *
 * A regra de “fit” usa o texto FINAL (formatado) como referência, garantindo que
 * o número nunca “estoure” o card durante a animação.
 */
export const KpiNumber: React.FC<KpiNumberProps> = ({
  value,
  format,
  compactFormat,
  fontPx = 20,
  autoFit = false,
  durationMs = 900,
  minFontPx = 14,
  maxFontPx = 30,
  breathingRoomPx = 6,
  disabled,
  className,
  as = "div",
  ariaLabel,
  changePercentage,
  trendIndicator,
  showComparison = false,
}) => {
  const safeValue = Number.isFinite(value) ? value : 0;

  const fullText = useMemo(() => {
    try {
      return format(safeValue);
    } catch {
      return String(safeValue);
    }
  }, [format, safeValue]);

  const inferredCompactFormat = useMemo<((n: number) => string) | null>(() => {
    // Não compactar percentuais (normalmente já são curtos e a leitura deve ser exata).
    if (fullText.includes("%")) return null;
    // Se parece moeda brasileira, usa compact currency; senão número compacto.
    if (fullText.includes("R$"))
      return (n: number) => formatCompact(n, "CURRENCY");
    return (n: number) => formatCompact(n, "NUMBER");
  }, [fullText]);

  const compactFormatFn = compactFormat ?? inferredCompactFormat;
  const compactText = useMemo(() => {
    if (!compactFormatFn) return null;
    try {
      return compactFormatFn(safeValue);
    } catch {
      return null;
    }
  }, [compactFormatFn, safeValue]);

  // Regra: sempre exibir o valor completo (sem compactação K, M, B)
  // para mostrar o valor cheio nos cards.
  const shouldPreferCompact = useMemo(() => {
    return false;
  }, []);

  const textForFit = shouldPreferCompact ? (compactText ?? fullText) : fullText;
  const displayFormat =
    shouldPreferCompact && compactFormatFn ? compactFormatFn : format;

  // Clamp do respiro: queremos um respiro curto e consistente entre monitores.
  const breathing = Math.max(0, Math.min(breathingRoomPx, 10));

  // Clamp de fonte: evita ficar ilegível.
  const effectiveMinFont = Math.max(minFontPx, 14);
  const effectiveMaxFont = Math.max(maxFontPx, effectiveMinFont);

  // Modo fixo (padrão): ignora min/max e respiro do fit.
  const fixedFont = Math.max(14, Math.round(fontPx));

  // Renderizar indicador de comparação
  const shouldShowComparison =
    showComparison && typeof changePercentage === "number" && trendIndicator;

  const TrendIcon =
    trendIndicator === "up"
      ? TrendingUp
      : trendIndicator === "down"
        ? TrendingDown
        : Minus;

  const trendColorClass =
    trendIndicator === "up"
      ? "text-success-soft"
      : trendIndicator === "down"
        ? "text-error-soft"
        : "text-muted-foreground";

  const Container = as === "span" ? "span" : "div";

  return (
    <Container className="flex flex-col">
      <AutoFitText
        as={as}
        textToFit={textForFit}
        title={fullText}
        minFontPx={autoFit ? effectiveMinFont : fixedFont}
        maxFontPx={autoFit ? effectiveMaxFont : fixedFont}
        breathingRoomPx={autoFit ? breathing : 0}
        enabled={!disabled && autoFit}
        className={cn(
          // Base "clean" e estável (melhora leitura em TV/monitor)
          "leading-none tracking-tight tabular-nums whitespace-nowrap overflow-hidden",
          // Peso padrão mais "soft"; telas podem sobrescrever via `className`.
          "font-medium",
          className,
        )}
      >
        <AnimatedNumber
          value={safeValue}
          durationMs={durationMs}
          format={displayFormat}
          disabled={disabled}
          ariaLabel={ariaLabel}
        />
      </AutoFitText>

      {shouldShowComparison && (
        <div
          className={cn(
            "flex items-center gap-1 mt-1 text-xs leading-4",
            trendColorClass,
          )}
        >
          <TrendIcon className="h-2.5 w-2.5" />
          <span className="font-medium">
            {formatSignedPercent(changePercentage!, 1)}
          </span>
        </div>
      )}
    </Container>
  );
};
